// ✅ هذا هو ملف "AddFarrierRecordInside.js"
import React, { useState, useEffect, useCallback } from 'react';
import {
  TextField,
  Button,
  FormControl,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  Box,
  Autocomplete,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { nowDateTimeInput } from '../../utils/dateUtils';

const apiUrl = process.env.REACT_APP_API_URL;

const normalize = (value = '') =>
  value
    .toString()
    .trim()
    .replace(/\u0640/g, '')
    .replace(/[\u0617-\u061A\u064B-\u0652]/g, '')
    .toLowerCase();

const AddFarrierRecordInside = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [horses, setHorses] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [medications, setMedications] = useState([]);
  const [selectedHorse, setSelectedHorse] = useState(null);

  const getLocalDateTime = () => {
    return nowDateTimeInput();
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) return '';
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age -= 1;
    }
    return age >= 0 ? age : '';
  };

  const localizedPair = (arabicValue, englishValue) => {
    if (i18n.language === 'ar') {
      return arabicValue || englishValue || '';
    }
    return englishValue || arabicValue || '';
  };

  const formatHorseGender = (value) => {
    const normalizedValue = String(value || '').trim().toLowerCase();
    if (!normalizedValue) return '';
    if (['s', 'stallion', 'male'].includes(normalizedValue)) return t('gender_stallion');
    if (['m', 'mare', 'f', 'female'].includes(normalizedValue)) return t('gender_mare');
    if (['g', 'gelding'].includes(normalizedValue)) return t('gender_gelding');
    return value;
  };

  const applyHorseToForm = (horse) => {
    if (!horse) {
      setSelectedHorse(null);
      setFormData((prevData) => ({
        ...prevData,
        horse_id: '',
        rfid_chip_number: '',
        owner_name: '',
        owner_phone: '',
        horse_name: '',
        owner_id: '',
        horse_color: '',
        horse_gender: '',
        horse_breed: '',
        horse_age: '',
      }));
      return;
    }

    setSelectedHorse(horse);
    setFormData((prevData) => ({
      ...prevData,
      horse_id: horse.horse_id,
      rfid_chip_number: horse.rfid_chip_number || '',
      owner_name: horse.owner_name || '',
      owner_phone: horse.owner_phone || '',
      horse_name: horse.horse_name || '',
      owner_id: horse.owner_id,
      horse_color: localizedPair(horse.color_arabic, horse.color_english),
      horse_gender: formatHorseGender(horse.gender),
      horse_breed: localizedPair(horse.breed_arabic, horse.breed_english),
      horse_age: calculateAge(horse.date_of_birth),
    }));
  };

  const [formData, setFormData] = useState({
    record_date: getLocalDateTime(),
    rfid_chip_number: '',
    horse_color: '',
    horse_gender: '',
    horse_breed: '',
    horse_age: '',
    total_amount: 0,
    notes: '',
    farrier_id: '',
    procedures: [],
    medications: [],
  });

  useEffect(() => {
    fetchDropdownData();
    fetchInsideHorses();
  }, []);

  const fetchDropdownData = async () => {
    try {
      const [proceduresRes, medicationsRes] = await Promise.all([
        api.get(`${apiUrl}/farrier_procedures`),
        api.get(`${apiUrl}/farrier_materials`),
      ]);
      setProcedures(proceduresRes.data);
      setMedications(medicationsRes.data);
    } catch (error) {
      console.error('Error fetching dropdown data', error);
    }
  };

  const fetchInsideHorses = async () => {
    try {
      const horsesRes = await api.get(`${apiUrl}/horses/inside`);
      setHorses(horsesRes.data);
    } catch (error) {
      console.error('Error fetching inside horses', error);
    }
  };

  const handleHorseChange = (_, horse) => {
    applyHorseToForm(horse);
  };

  const calculateTotalAmount = useCallback(() => {
    const proceduresTotal = formData.procedures.reduce((sum, procId) => {
      const proc = procedures.find((p) => p.procedure_id === procId);
      return sum + (Number(proc?.price) || 0);
    }, 0);

    const materialsTotal = formData.medications.reduce((sum, material) => {
      const quantity = Number(material.quantity) || 0;
      const unitPrice = Number(material.price) || 0;
      return sum + unitPrice * quantity;
    }, 0);

    setFormData((prevState) => ({
      ...prevState,
      total_amount: proceduresTotal + materialsTotal,
    }));
  }, [formData.procedures, formData.medications, procedures]);

  useEffect(() => {
    calculateTotalAmount();
  }, [formData.procedures, formData.medications, calculateTotalAmount]);

  const handleProcedureChange = (procedure_id) => {
    setFormData((prevData) => {
      const isChecked = prevData.procedures.includes(procedure_id);
      const updatedProcedures = isChecked
        ? prevData.procedures.filter((id) => id !== procedure_id)
        : [...prevData.procedures, procedure_id];

      return { ...prevData, procedures: updatedProcedures };
    });
  };

  const handleMedicationChange = (index, field, value) => {
    setFormData((prevData) => {
      const updatedMedications = prevData.medications.map((med, i) => {
        if (i === index) {
          const selectedMed = medications.find(
            (m) => m.medication_id === parseInt(value)
          );
          if (field === 'medication_id' && selectedMed) {
            return {
              ...med,
              medication_id: selectedMed.medication_id,
              quantity: selectedMed.default_quantity || 1,
              price: Number(selectedMed.price) || 0,
            };
          }
          return { ...med, [field]: value };
        }
        return med;
      });
      return { ...prevData, medications: updatedMedications };
    });
  };

  const addMedication = () => {
    setFormData((prevData) => ({
      ...prevData,
      medications: [
        ...prevData.medications,
        { medication_id: '', quantity: '', price: 0 },
      ],
    }));
  };

  const removeMedication = (index) => {
    setFormData((prevData) => ({
      ...prevData,
      medications: prevData.medications.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    try {
      const user_id = localStorage.getItem('userId');
      if (!user_id) {
        alert('User not logged in');
        return;
      }

      const payload = {
        ...formData,
        work_place: 'Inside',
        horse_name: selectedHorse?.horse_name,
        inside_horse_id: selectedHorse?.horse_id,
        outside_horse_id: null,
        farrier_id: user_id,
        owner_id: selectedHorse?.owner_id,
        procedures: formData.procedures.map((procId) => {
          const proc = procedures.find((p) => p.procedure_id === procId);
          return {
            procedure_id: procId,
            price: proc?.price || 0,
          };
        }),
        medications: formData.medications.map((med) => ({
          medication_id: med.medication_id,
          quantity: med.quantity,
          dosage: null,
          unit: null,
          price: (Number(med.price) || 0) * (Number(med.quantity) || 0),
        })),
      };

      console.log('submitting record payload', payload);

      await api.post(`${apiUrl}/farrier-records`, payload);

      alert('Record added successfully!');
      navigate('/FarrierList');
    } catch (error) {
      console.error('Error submitting record:', error);
      alert(error?.response?.data?.error || 'Failed to save record. Please try again.');
    }
  };
  const handleSearchByChip = async () => {
    try {
      const res = await api.get(`${apiUrl}/horses/inside`);
      const horse = res.data.find(
        (h) => h.rfid_chip_number === formData.rfid_chip_number
      );

      if (horse) {
        applyHorseToForm(horse);
      } else {
        alert(t('Horse not found'));
      }
    } catch (error) {
      console.error('Error searching horse by chip:', error);
      alert(t('Failed to search for horse'));
    }
  };

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom textAlign="center">
        {t('Add_New_Farrier_Record')}
      </Typography>

      <TextField
        label={t('Date')}
        type="datetime-local"
        name="record_date"
        value={formData.record_date}
        fullWidth
        margin="normal"
        disabled
      />

      <FormControl fullWidth margin="normal">
        <Autocomplete
          options={horses}
          value={selectedHorse}
          onChange={handleHorseChange}
          getOptionLabel={(option) => option?.horse_name || ''}
          isOptionEqualToValue={(option, value) =>
            String(option.horse_id) === String(value.horse_id)
          }
          filterOptions={(options, state) => {
            const q = normalize(state.inputValue || '');
            return options.filter(
              (horse) =>
                normalize(horse.horse_name || '').includes(q) ||
                normalize(horse.rfid_chip_number || '').includes(q)
            );
          }}
          renderInput={(params) => (
            <TextField {...params} label={t('select_horse')} margin="normal" fullWidth />
          )}
          ListboxProps={{ style: { maxHeight: 300 } }}
          clearOnBlur={false}
        />
      </FormControl>
      <Grid container spacing={3} alignItems="center" mt={0.5}>
        <Grid item xs={11}>
          <TextField
            label={t('rfid_chip_number')}
            value={formData.rfid_chip_number}
            onChange={(e) =>
              setFormData({ ...formData, rfid_chip_number: e.target.value })
            }
            fullWidth
            margin="dense"
          />
        </Grid>
        <Grid item xs={1}>
          <Box display="flex" alignItems="center" justifyContent="center">
            <IconButton onClick={handleSearchByChip} aria-label={t('search')}>
              <SearchIcon />
            </IconButton>
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label={t('color')} value={formData.horse_color || ''} fullWidth margin="normal" disabled />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label={t('gender')} value={formData.horse_gender || ''} fullWidth margin="normal" disabled />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label={t('breed')} value={formData.horse_breed || ''} fullWidth margin="normal" disabled />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField label={t('age')} value={formData.horse_age || ''} fullWidth margin="normal" disabled />
        </Grid>
      </Grid>

      <TextField
        label={t('owner_name')}
        value={formData.owner_name || ''}
        disabled
        fullWidth
        margin="normal"
      />
      <TextField
        label={t('phone')}
        value={formData.owner_phone || ''}
        disabled
        fullWidth
        margin="normal"
      />

      <FormControl component="fieldset" margin="normal">
        <Typography>Procedures</Typography>
        <Grid container spacing={2}>
          {procedures.map((procedure) => (
            <Grid item xs={12} sm={6} md={4} key={procedure.procedure_id}>
              <FormControlLabel
                control={
                  <Checkbox
                    value={procedure.procedure_id}
                    checked={formData.procedures.includes(
                      procedure.procedure_id
                    )}
                    onChange={() =>
                      handleProcedureChange(procedure.procedure_id)
                    }
                  />
                }
                label={procedure.procedure_name}
              />
            </Grid>
          ))}
        </Grid>
      </FormControl>
      {true && (
        <>
          <FormControl component="fieldset" margin="normal" sx={{ width: '100%', gap: 1.5 }}>
            <Typography>{t('farrierMaterials.title')}</Typography>
            {formData.medications.map((medication, index) => (
              <Box key={index} sx={{ mb: 1.5 }}>
                <Autocomplete
                  options={medications}
                  value={
                    medications.find(
                      (med) => String(med.medication_id) === String(medication.medication_id)
                    ) || null
                  }
                  onChange={(_, selectedMaterial) =>
                    handleMedicationChange(
                      index,
                      'medication_id',
                      selectedMaterial?.medication_id || ''
                    )
                  }
                  isOptionEqualToValue={(option, value) =>
                    String(option.medication_id) === String(value.medication_id)
                  }
                  getOptionLabel={(option) =>
                    option?.medication_name
                      ? `${option.medication_name} - ${t('farrierMaterials.stock')}: ${option.stock_quantity ?? 0}`
                      : ''
                  }
                  getOptionDisabled={(option) => Number(option.stock_quantity || 0) <= 0}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('farrierMaterials.name')}
                      margin="dense"
                      fullWidth
                    />
                  )}
                  fullWidth
                />
                <TextField
                  label={t('farrierMaterials.quantity')}
                  name="quantity"
                  value={medication.quantity}
                  onChange={(e) =>
                    handleMedicationChange(index, 'quantity', e.target.value)
                  }
                  fullWidth
                  margin="dense"
                />
                <Button variant="contained" color="secondary" onClick={() => removeMedication(index)}>
                  {t('delete')}
                </Button>
              </Box>
            ))}
            <Button variant="contained" color="primary" onClick={addMedication}>
              {t('farrierMaterials.addNew')}
            </Button>
          </FormControl>
        </>
      )}

      <TextField
        label="Notes"
        name="notes"
        value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        fullWidth
        multiline
        rows={4}
        margin="normal"
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        fullWidth
      >
        Submit
      </Button>
    </Box>
  );
};

export default AddFarrierRecordInside;
